import React from 'react'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { Create } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { TextField } from '@common/components/Form/TextField'
import { SPACING } from '@common/theme/spacing'
import { useSaveAdditionalComment } from '@features/subject/hooks/useSaveAdditionalComment'
import { handleMutationError } from '@common/hooks/handleMutationError'
import Toast from '../../../../common/components/Toast'
import { AxiosError } from 'axios'

export type AdditionalCommentSectionProps = {
  containerSubmissionId: string
  additionalComment?: string
}

const AdditionalCommentSchema = z.object({
  comment: z.string().optional().default(''),
})

type AdditionalComment = z.infer<typeof AdditionalCommentSchema>

/**
 * Dark-mode safe version:
 *  - No hardcoded colors; uses theme tokens (background.paper, divider, text.secondary)
 *  - Consistent container UI in light & dark
 *  - Proper form submit (preventDefault) and optimistic state update on success
 */
export default function AdditionalCommentSection({
  containerSubmissionId,
  additionalComment,
}: AdditionalCommentSectionProps) {
  const { studyId } = useParams()

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<AdditionalComment>({
    defaultValues: { comment: additionalComment ?? '' },
    mode: 'onChange',
    resolver: zodResolver(AdditionalCommentSchema),
  })

  // Keep a local, single source of truth for the read-only view
  const [currentComment, setCurrentComment] = React.useState<string>(additionalComment ?? '')
  const [isEditable, setIsEditable] = React.useState<boolean>(!(additionalComment && additionalComment.length > 0))

  const { mutateAsync: saveComment } = useSaveAdditionalComment()

  const onSave = async () => {
    const value = getValues('comment') ?? ''

    // Optimistic UI update
    setCurrentComment(value)
    setIsEditable(false)

    try {
      await saveComment({
        body: {
          container_submission_id: containerSubmissionId,
          additional_comments: value,
        },
        path: { study_id: studyId! },
      })

      toast(<Toast message="Comment saved." variant="success" />)
    } catch (error) {
      setIsEditable(true)

      if (error instanceof AxiosError) {
        handleMutationError(error)
      } else {
        console.error(error)
      }
    }
  }

  const SectionContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: SPACING.borderRadiusXl,
        p: SPACING.spacingLg,
        mt: SPACING.spacingLg,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  )

  if (isEditable) {
    return (
      <SectionContainer>
        <form
          onSubmit={handleSubmit(async (/* data */) => {
            await onSave()
          })}
          noValidate
        >
          <TextField
            name="comment"
            control={control}
            label="Additional comment"
            multiline
            rows={6}
            fullWidth
            helperText="Please type the additional comment here and save it."
            sx={{ mb: SPACING.minSpacing }}
            size="small"
            data-testid="comment"
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              color="secondary"
              data-testid="save-button"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </form>
      </SectionContainer>
    )
  }

  return (
    <SectionContainer>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Additional Comment
        </Typography>
        <IconButton
          data-testid="edit-button"
          color="default"
          aria-label="Edit additional comment"
          onClick={() => setIsEditable(true)}
          size="small"
        >
          <Create fontSize="small" />
        </IconButton>
      </Stack>
      <Typography data-testid="uneditable-comment" sx={{ whiteSpace: 'pre-wrap' }}>
        {currentComment || '—'}
      </Typography>
    </SectionContainer>
  )
}
